import { askConfirm } from '@/components/ui/ConfirmHost';
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getCbtExams, getClassesStudents, postCbtExamsPushKeyed } from '@/api/teacher';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Banner, PageHeader, QueryState, Select } from '@/components/kit/ui';
import { asList, errMsg, unwrap } from '@/components/kit/data';
import { ClassSelect, newKey } from '../shared';

interface Exam { id: number; title: string; status: string; questions: number; class_ids: number[]; opens_at: string | null }

export function CbtPushPage() {
  const qc = useQueryClient();
  const [id, setId] = useState('');
  const [target, setTarget] = useState<'class' | 'students'>('class');
  const [classId, setClassId] = useState('');
  const [picked, setPicked] = useState<number[]>([]);
  const [code, setCode] = useState(false);
  const [msg, setMsg] = useState<{ tone: 'success' | 'error'; text: string } | null>(null);
  const [result, setResult] = useState<{ access_code: string | null; students: number; notified: number; status: string } | null>(null);
  const q = useQuery({ queryKey: ['teacher-cbt-exams'], queryFn: () => getCbtExams({ per_page: 100 }).then((r) => asList<Exam>(unwrap(r))) });
  const students = useQuery({ queryKey: ['teacher-class-students', classId], enabled: target === 'students' && !!classId, queryFn: () => getClassesStudents(classId).then((r) => unwrap<{ students: { id: number; name: string; admission_no: string }[] }>(r).students) });
  const pushable = (q.data ?? []).filter((e) => !['closed', 'released'].includes(e.status));
  const exam = pushable.find((e) => String(e.id) === id);

  const push = useMutation({
    mutationFn: () => postCbtExamsPushKeyed(id, { target, ...(target === 'students' ? { target_ids: picked } : {}), require_code: code }, newKey()),
    onSuccess: (r) => { setResult(unwrap(r)); setMsg({ tone: 'success', text: (r.data as { message?: string }).message ?? 'Test pushed.' }); qc.invalidateQueries({ queryKey: ['teacher-cbt-exams'] }); },
    onError: (e) => setMsg({ tone: 'error', text: errMsg(e) }),
  });

  return (
    <div className="space-y-4">
      <PageHeader title="Push a test" subtitle="Open a prepared test to its classes or to chosen students, and notify them." />
      {msg && <Banner tone={msg.tone}>{msg.text}</Banner>}
      {result && (
        <Card title="Test sent">
          <p className="text-sm">Status: <b>{result.status}</b> · {result.students} student(s), {result.notified} notified.</p>
          {result.access_code && <p className="text-sm mt-2">Test code: <span className="font-mono text-2xl font-bold tracking-widest">{result.access_code}</span> — read it out when students should start.</p>}
        </Card>
      )}
      <Card>
        <QueryState q={q} isEmpty={!pushable.length} empty="You have no open tests. Create one under CBT Create first.">
          <div className="space-y-4 max-w-2xl">
            <Select label="Test" value={id} onChange={(e) => { setId(e.target.value); setResult(null); }}>
              <option value="">Select a test</option>
              {pushable.map((e) => <option key={e.id} value={e.id}>{e.title} · {e.status} · {e.questions} questions</option>)}
            </Select>
            {exam && exam.questions === 0 && <Banner tone="warn">Add questions to this test before pushing it.</Banner>}
            <div className="flex gap-4 text-sm">
              <label className="flex items-center gap-2"><input type="radio" checked={target === 'class'} onChange={() => setTarget('class')} /> Its classes ({exam?.class_ids.length ?? 0})</label>
              <label className="flex items-center gap-2"><input type="radio" checked={target === 'students'} onChange={() => setTarget('students')} /> Chosen students</label>
            </div>
            {target === 'students' && (
              <div className="space-y-2">
                <ClassSelect value={classId} onChange={(v) => { setClassId(v); setPicked([]); }} />
                {classId && <QueryState q={students} isEmpty={!students.data?.length} empty="No students in this class.">
                  <div className="grid sm:grid-cols-2 gap-1 max-h-64 overflow-y-auto border rounded-xl p-2">
                    {(students.data ?? []).map((s) => <label key={s.id} className="flex items-center gap-2 text-sm"><input type="checkbox" checked={picked.includes(s.id)} onChange={(e) => setPicked((p) => (e.target.checked ? [...p, s.id] : p.filter((x) => x !== s.id)))} />{s.name} <span className="text-xs text-slate-400">{s.admission_no}</span></label>)}
                  </div>
                </QueryState>}
              </div>
            )}
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={code} onChange={(e) => setCode(e.target.checked)} /> Require a test code (students enter it to start)</label>
            <Button disabled={!exam || exam.questions === 0 || push.isPending || (target === 'students' && !picked.length)} onClick={async () => { if (await askConfirm('Students can start writing as soon as it reaches them.', { title: 'Push this test now?', confirmLabel: 'Push test' })) push.mutate(); }}>{push.isPending ? 'Pushing…' : 'Push test'}</Button>
          </div>
        </QueryState>
      </Card>
    </div>
  );
}
